import React, { useEffect, useRef, useState } from 'react';
import './EngagementWidgetHost.css';

const WIDGET_SOURCE = 'zynity_engagement_widget';

const collapsedSize = { width: 112, height: 56 };

function toSafeNumber(value, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return parsed;
}

export const EngagementWidgetHost = ({ enabled }) => {
  const frameRef = useRef(null);
  const [size, setSize] = useState(enabled ? collapsedSize : { width: 0, height: 0 });
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [widgetReady, setWidgetReady] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setSize({ width: 0, height: 0 });
      setFrameLoaded(false);
      setWidgetReady(false);
      return;
    }
    setSize((prev) => {
      if (prev.width > 0 && prev.height > 0) return prev;
      return collapsedSize;
    });
  }, [enabled]);

  useEffect(() => {
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || data.source !== WIDGET_SOURCE) return;

      const frameWindow = frameRef.current?.contentWindow;
      if (frameWindow && event.source !== frameWindow) return;

      if (data.type === 'ready') {
        setWidgetReady(true);
        return;
      }

      if (data.type !== 'state') return;

      const width = toSafeNumber(data.width, collapsedSize.width);
      const height = toSafeNumber(data.height, collapsedSize.height);
      setSize({ width, height });
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    // Safety fallback: if message is delayed/missed, reveal shortly after iframe load.
    if (!enabled || !frameLoaded || widgetReady) return undefined;
    const timer = setTimeout(() => setWidgetReady(true), 1200);
    return () => clearTimeout(timer);
  }, [enabled, frameLoaded, widgetReady]);

  if (!enabled) {
    return null;
  }

  const isVisible = frameLoaded && widgetReady && size.width > 0 && size.height > 0;

  return (
    <div className="engagement-widget-host" aria-hidden={size.width === 0 || size.height === 0}>
      <iframe
        ref={frameRef}
        className={`engagement-widget-host-frame${isVisible ? ' is-ready' : ''}`}
        src="/engagement/widget"
        title="Zyni Assistant"
        style={{
          width: `${size.width}px`,
          height: `${size.height}px`,
        }}
        onLoad={() => setFrameLoaded(true)}
        allow="clipboard-write; microphone"
      />
    </div>
  );
};
